import { apiClient } from "@/config/api.config";
import { toast } from "react-toastify";

const BLOG_STORAGE_KEY = "blog_form_data";

const blogService = {
  // Get all blog posts (public)
  getPosts: async (page = 1, limit = 10, filters = {}) => {
    try {
      const queryParams = new URLSearchParams({
        page,
        limit,
        ...filters,
      });

      const response = await apiClient.get(`/blogs/posts?${queryParams}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || new Error("Failed to fetch blog posts");
    }
  },

  getPostById: async (id) => {
    try {
      const response = await apiClient.get(`/blogs/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || new Error("Failed to fetch blog post");
    }
  },
  // ✅ New: Fetch blog categories
  getCategories: async () => {
    try {
      const response = await apiClient.get("/blogs/category");
      return response.data;
    } catch (error) {
      throw error.response?.data || new Error("Failed to fetch categories");
    }
  },

  // ✅ New: Fetch blog tags
  getTags: async () => {
    try {
      const response = await apiClient.get("/blogs/tag");
      return response.data;
    } catch (error) {
      throw error.response?.data || new Error("Failed to fetch tags");
    }
  },
  // Get a single post by slug
  getPostBySlug: async (slug) => {
    try {
      const response = await apiClient.get(`/blogs/posts/${slug}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || new Error("Failed to fetch blog post");
    }
  },

  // Get related posts
  getRelatedPosts: async (id) => {
    try {
      const response = await apiClient.get(`/blogs/posts/${id}/related`);
      return response.data;
    } catch (error) {
      throw error.response?.data || new Error("Failed to fetch related posts");
    }
  },

  getUserBlogs: async (page = 1, perPage = 10, filters = {}) => {
    try {
      // Create query parameters
      const queryParams = new URLSearchParams();
      queryParams.append("page", page);
      queryParams.append("perPage", perPage);

      // Add search parameter if it exists
      if (filters.search) {
        queryParams.append("search", filters.search);
      }

      // Add status filter if it exists
      if (filters.status) {
        queryParams.append("status", filters.status);
      }

      const response = await apiClient.get(
        `/blogs/my-blogs?${queryParams.toString()}`
      );

      return response.data;
    } catch (error) {
      throw error.response?.data || new Error("Failed to fetch user blogs");
    }
  },

  createPost: async (formData) => {
    try {
      const form = new FormData();

      // Add text fields directly
      form.append("title", formData.title);
      form.append("content", formData.content);
      form.append("excerpt", formData.excerpt);
      form.append("status", formData.status);

      // Handle categories
      if (formData.categoryId) {
        // If it's an array, append each element, otherwise append the single value
        if (Array.isArray(formData.categoryId)) {
          formData.categoryId.forEach((cat) => form.append("categories", cat));
        } else {
          form.append("categories", formData.categoryId);
        }
      }

      // Handle tags
      if (formData.tags) {
        // Filter out any empty or undefined values
        const validTags = Array.isArray(formData.tags)
          ? formData.tags.filter((tag) => tag && tag.toString().trim() !== "")
          : formData.tags && formData.tags.toString().trim() !== ""
            ? [formData.tags]
            : [];

        validTags.forEach((tag) => form.append("tags", tag));
      }

      // Add featured image if it exists
      if (formData.featuredImage instanceof File) {
        form.append("featuredImage", formData.featuredImage);
      }

      // Override the default content-type for FormData
      const response = await apiClient.post("/blogs/posts", form, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.status === 201) {
        toast.success("Post Successfully Created!");
      }

      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to create post");
      throw error.response?.data || new Error("Failed to create post");
    }
  },

  updatePost: async (id, formData) => {
    try {
      const form = new FormData();

      // Add text fields
      form.append("title", formData.title);
      form.append("content", formData.content);
      form.append("excerpt", formData.excerpt);
      form.append("status", formData.status);
      form.append("categories", formData.categoryId);

      // Handle tags array correctly
      if (formData.tags && formData.tags.length > 0) {
        formData.tags.forEach((tag) => {
          form.append("tags", tag);
        });
      }

      // Add featured image if it exists
      if (formData.featuredImage instanceof File) {
        form.append("featuredImage", formData.featuredImage);
      }

      const response = await apiClient.put(`/blogs/posts/${id}`, form, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      return response.data;
    } catch (error) {
      throw error.response?.data || new Error("Failed to update blog post");
    }
  },

  // Delete a blog post
  deletePost: async (id) => {
    try {
      const response = await apiClient.delete(`/blogs/posts/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || new Error("Failed to delete blog post");
    }
  },

  // Toggle like on a post
  toggleLike: async (postId) => {
    try {
      const response = await apiClient.post(`/blogs/posts/${postId}/like`, {});
      return response.data;
    } catch (error) {
      throw error.response?.data || new Error("Failed to toggle like");
    }
  },

  // Local storage operations
  getSavedBlogData: () => {
    try {
      const savedData = localStorage.getItem(BLOG_STORAGE_KEY);
      return savedData ? JSON.parse(savedData) : null;
    } catch (error) {
      return null;
    }
  },

  saveBlogData: (data) => {
    try {
      localStorage.setItem(BLOG_STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      // Silent error handling
    }
  },

  clearSavedBlogData: () => {
    localStorage.removeItem(BLOG_STORAGE_KEY);
  },

  getStats: async () => {
    try {
      const response = await apiClient.get(`/blogs/blogs/stats`);
      return response.data;
    } catch (error) {
      throw (
        error.response?.data || new Error("Failed to fetch blog statistics")
      );
    }
  },

  // Get all tags
  getTags: async () => {
    try {
      const response = await apiClient.get(`/blogs/tag`);
      return response.data;
    } catch (error) {
      throw error.response?.data || new Error("Failed to fetch tags");
    }
  },

  // Get popular tags
  getPopularTags: async () => {
    try {
      const response = await apiClient.get(`/blogs/tag/popular`);
      return response.data;
    } catch (error) {
      throw error.response?.data || new Error("Failed to fetch popular tags");
    }
  },

  // Get a tag by its slug
  getTagBySlug: async (slug) => {
    try {
      const response = await apiClient.get(`/blogs/tag/${slug}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || new Error("Failed to fetch tag by slug");
    }
  },

  // Get all categories
  getCategories: async () => {
    try {
      const response = await apiClient.get(`/blogs/category`);
      return response.data;
    } catch (error) {
      throw error.response?.data || new Error("Failed to fetch categories");
    }
  },

  // Get a category by its slug
  getCategoryBySlug: async (slug) => {
    try {
      const response = await apiClient.get(`/blogs/category/${slug}`);
      return response.data;
    } catch (error) {
      throw (
        error.response?.data || new Error("Failed to fetch category by slug")
      );
    }
  },
};

export default blogService;
